import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = false, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-muted-foreground w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        // cell: 'h-10 w-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-primary-200/50 [&:has([aria-selected])]:border-t-[2px] [&:has([aria-selected])]:border-b-[2px] [&:has([aria-selected])]:border-dashed [&:has([aria-selected])]:border-black  first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full focus-within:relative focus-within:z-20',
        // cell: 'h-10 w-10 text-center text-sm p-0 relative focus-within:relative focus-within:z-20  [&:has([aria-selected])]:border-t-[1.5px]  [&:has([aria-selected])]:border-dotted  border-primary-500 [&:has([aria-selected])]:border-b-[1px] first:[&:has([aria-selected])]:border-l-[1px]  last:[&:has([aria-selected])]:border-r-[1px] [&:has([aria-selected].day-range-end)]:border-r-[1px]  [&:has([aria-selected].day-range-end)]:!rounded-r-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full',
        // cell: 'h-[2.4rem] w-[2.4rem] text-center text-sm p-0 relative focus-within:relative focus-within:z-20 \
        // [&:not(:has([aria-selected]))]:hover:rounded-full [&:not(:has([aria-selected]))]:hover:ring-[1.5px] [&:not(:has([aria-selected]))]:ring-primary-500 \
        // [&:has([aria-selected])]:border-t-[1.5px] [&:has([aria-selected])]:border-b-[1.5px] \
        // [&:has([aria-selected])]:border-dashed border-primary-500 \
        // first:[&:has([aria-selected])]:border-l-[1.5px] \
        // [&:not(:first-child):has([aria-selected])]:border-l-0 \
        // last:[&:has([aria-selected])]:border-r-[1.5px] \
        // [&:has([aria-selected].day-range-end)]:!rounded-r-full \
        // first:[&:has([aria-selected])]:rounded-l-full \
        // last:[&:has([aria-selected])]:rounded-r-full \
        // [&:has([aria-selected].day-range-start)]:!rounded-l-full \
        // [&:has([aria-selected])]:bg-primary-100 \
        // [&:has([aria-selected].day-range-start)]:!border-l-[1.5px] ',
        cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20 \
        [&:has([aria-selected])]:border-dashed border-primary-500 \
        [&:not(:first-child):has([aria-selected])]:border-l-0 \
        [&:has([aria-selected].day-range-end)]:!rounded-r-full \
        first:[&:has([aria-selected])]:rounded-l-full \
        last:[&:has([aria-selected])]:rounded-r-full \
        [&:has([aria-selected].day-range-start)]:!rounded-l-full \
        [&:has([aria-selected])]:bg-primary-100\
        [&[aria-label^="1 "]]:rounded-l-full \
        [&[aria-label$="31"]]:rounded-r-full',

        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 font-normal aria-selected:opacity-100 !rounded-full ',
          '        [&:not(:has([aria-selected]))]:hover:rounded-full [&:not(:has([aria-selected]))]:hover:ring-[1.5px] [&:not(:has([aria-selected]))]:ring-primary-500 ring-inset'
        ),
        day_range_end: 'day-range-end !ring-0',
        day_range_start: 'day-range-start !ring-0',
        day_selected:
          'bg-primary-600 text-white hover:bg-primary-600 hover:text-white focus:bg-primary-600 focus:text-white !rounded-full !ring-0',
        day_today: '!border-gray-600 ring-[1px] !ring-black ring-inset hover:!ring-primary-600',
        day_outside: 'opacity-0 text-muted-foreground cursor-default pointer-events-none hidden bg-white',
        day_disabled: 'text-muted-foreground opacity-50 border-none',
        day_range_middle:
          'border-[1.5px] border-transparent aria-selected:border-primary-100 aria-selected:text-black dark:aria-selected:text-white aria-selected:rounded-full !bg-transparent hover:!bg-primary-200 rounded-full',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
